import * as THREE from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';
import * as csmShaderModule from 'three/examples/jsm/csm/CSMShader.js';

// three r186's CSM ships an older copy of lights_fragment_begin that never fills `material.dfg` (the
// DFG lookup the image-based specular now reads) or the multi-scattering compensation, so with CSM
// on, environment reflections vanished and metallic glass went black. Splice the core block in
// (skipped once the example catches up).
(() => {
  const CSMShader = (csmShaderModule as unknown as { CSMShader: { lights_fragment_begin: string } }).CSMShader;
  const core = THREE.ShaderChunk.lights_fragment_begin;
  const csm = CSMShader.lights_fragment_begin;
  if (csm.includes('material.dfg')) return;
  const start = core.indexOf('#ifdef STANDARD');
  const end = core.indexOf('IncidentLight directLight;');
  const at = csm.indexOf('IncidentLight directLight;');
  if (start < 0 || end < start || at < 0) return;
  CSMShader.lights_fragment_begin = csm.slice(0, at) + core.slice(start, end) + csm.slice(at);
})();

export interface ShadowOptions {
  cascades: number;
  /** Shadowed distance from the camera (m). */
  maxFar: number;
  mapSize: number;
}

/**
 * Cascaded sun shadows (three's CSM). CSM brings its own directional lights (one per cascade), which
 * replace the sky rig's sun while shadows are on. Every lit material goes through `setup`, which chains
 * CSM's shader hook with any hook the material already has (the glass cut-out injects its own).
 */
export class Shadows {
  private csm: CSM | null = null;
  private readonly materials = new Set<THREE.Material>();

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly sun: THREE.DirectionalLight,
    private readonly sunDir: THREE.Vector3,
  ) {}

  get enabled(): boolean {
    return this.csm !== null;
  }

  enable(o: ShadowOptions): void {
    this.disable();
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.csm = new CSM({
      camera: this.camera,
      parent: this.scene,
      cascades: o.cascades,
      maxFar: o.maxFar,
      shadowMapSize: o.mapSize,
      lightDirection: this.sunDir.clone().negate(),
      lightIntensity: this.sun.intensity,
      lightNear: 1,
      lightFar: 3000,
      // Tall towers outside a cascade's slice must still cast into it.
      lightMargin: 400,
      shadowBias: -0.0002,
      mode: 'practical',
    });
    for (const l of this.csm.lights) {
      l.color.copy(this.sun.color);
      l.shadow.normalBias = 0.04;
    }
    this.sun.visible = false;
    for (const m of this.materials) this.hook(m);
  }

  disable(): void {
    if (!this.csm) return;
    this.csm.remove();
    this.csm.dispose();
    this.csm = null;
    this.renderer.shadowMap.enabled = false;
    this.sun.visible = true;
    for (const m of this.materials) {
      if (m.defines) {
        delete m.defines.USE_CSM;
        delete m.defines.CSM_CASCADES;
        delete m.defines.CSM_FADE;
      }
      m.onBeforeCompile = m.userData.ownHook ?? (() => {});
      m.needsUpdate = true;
    }
  }

  /** Register a lit material (idempotent). */
  setup(m: THREE.Material): THREE.Material {
    if (!this.materials.has(m)) {
      m.userData.ownHook = m.onBeforeCompile;
      this.materials.add(m);
      m.addEventListener('dispose', () => {
        this.materials.delete(m);
        this.csm?.shaders.delete(m);
      });
    }
    if (this.csm) this.hook(m);
    return m;
  }

  private hook(m: THREE.Material): void {
    const own = m.userData.ownHook as THREE.Material['onBeforeCompile'] | undefined;
    this.csm!.setupMaterial(m);
    const csmHook = m.onBeforeCompile;
    m.onBeforeCompile = (shader, renderer) => {
      csmHook.call(m, shader, renderer);
      own?.call(m, shader, renderer);
    };
    m.needsUpdate = true;
  }

  update(): void {
    this.csm?.update();
  }

  resize(): void {
    this.csm?.updateFrustums();
  }
}
